import React, { Fragment } from "react";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
} from "react-router-dom";
import PropTypes from "prop-types";
import history from "./history";
import MainLayout from "../layouts/MainLayout/MainLayout";
import { useSelector } from "react-redux";
import NotFound from "../pages/NotFound/NotFound";
import ErrorPage from "../pages/ErrorPage";
import { ROUTE_CONFIG, getDefaultRoute } from "../config/routeConfig";

// Generate routes from centralized config
const routes = [
  { path: "/error", component: ErrorPage, layout: Fragment },
  {
    path: "/",
    component: React.lazy(() => import("../pages/Login")),
    layout: MainLayout,
  },
  {
    path: "/unauthorized",
    component: React.lazy(() => import("../pages/Unauthorized")),
    layout: MainLayout,
  },
  { path: "*", component: NotFound, layout: Fragment },
  // Dynamic routes from config
  ...Object.entries(ROUTE_CONFIG).map(([path, config]) => ({
    path,
    component: React.lazy(() => import(`../pages/${config.component}`)),
    layout: config.layout === "MainLayout" ? MainLayout : Fragment,
    allowedRoles: config.allowedRoles,
  })),
];

const Element = ({ component, layout, allowedRoles = [] }) => {
  const { isAuthenticated, role } = useSelector(state => state.auth);
  const UnauthorizedComponent = React.lazy(
    () => import("../pages/Unauthorized"),
  );
  let Component = UnauthorizedComponent;

  // Redirect logic for root path based on role when authenticated
  if (isAuthenticated && location.pathname === "/") {
    const defaultRoute = getDefaultRoute(role);
    return <Navigate to={defaultRoute} replace />;
  }

  if (allowedRoles.length === 0 || allowedRoles.includes(role)) {
    Component = component;
  }

  const Layout = layout || Fragment;

  return (
    <Layout>
      <Component />
    </Layout>
  );
};

Element.propTypes = {
  component: PropTypes.any,
  layout: PropTypes.func,
  allowedRoles: PropTypes.array,
};

const createRoutes = routes => {
  return routes.map(route => {
    if (route.routes?.length) {
      return (
        <Route
          key={route.path}
          path={route.path}
          element={<Element {...route} />}
          index={route.index}
        >
          {createRoutes(route.routes)}
        </Route>
      );
    } else {
      // Render regular route
      return (
        <Route
          key={route.path}
          path={route.path}
          element={<Element {...route} />}
          index={route.index}
        />
      );
    }
  });
};

export default function AppRouter() {
  return (
    <Router location={history.location} navigator={history}>
      <Routes>{createRoutes(routes)}</Routes>
    </Router>
  );
}
